import {RefObject, useCallback, useEffect, useState} from "react";

import debounce from "debounce";

import {useDevice} from "@/shared/utils/device/device-context";

/** Allowed maxLines values (must match CSS classes) */
export type MaxLinesValue = 0 | 2 | 3 | 4 | 6 | 8 | 10 | 12 | 14 | 16;

export interface ExpandableTextState {
    isExpanded: boolean;
    canExpand: boolean;
    collapsedHeight: number;
    expandedHeight: number;
    isReady: boolean;
    showEllipsis: boolean;
    /** True after expand animation completes - allows using auto height */
    isExpandAnimationComplete: boolean;
}

/** SSR estimation config per device type */
export interface DeviceEstimation {
    /** Container width in pixels */
    width: number;
    /** Font size in pixels (default: 14) */
    fontSize?: number;
}

/** SSR estimation settings for different devices */
export interface SSREstimation {
    mobile?: DeviceEstimation;
    tablet?: DeviceEstimation;
    desktop?: DeviceEstimation;
}

/** Max lines per device type */
export interface MaxLinesConfig {
    mobile?: MaxLinesValue;
    tablet?: MaxLinesValue;
    desktop?: MaxLinesValue;
}

/** Default max lines */
const DEFAULT_MAX_LINES = {
    mobile: 4,
    tablet: 6,
    desktop: 6,
} as const;

/** Default SSR estimation values */
const DEFAULT_SSR_ESTIMATION = {
    mobile: {width: 430, fontSize: 14},
    tablet: {width: 991, fontSize: 14},
    desktop: {width: 1280, fontSize: 14},
} as const;

export interface UseExpandableTextOptions {
    containerRef: RefObject<HTMLElement | null>;
    /** Character count of the content */
    charCount: number;
    /**
     * Maximum lines to show when collapsed.
     * Can be a number or config per device type.
     * @example maxLines={6}
     * @example maxLines={{ mobile: 4, tablet: 6, desktop: 8 }}
     */
    maxLines?: MaxLinesValue | MaxLinesConfig;
    /** SSR estimation config per device type */
    ssrEstimation?: SSREstimation;
}

export interface UseExpandableTextReturn extends ExpandableTextState {
    toggle: () => void;
    /** Current maxLines based on device type */
    maxLines: MaxLinesValue;
}

/**
 * Estimate if text will need expansion based on character count and container width
 * Used for SSR to predict button visibility before client-side measurement
 */
function estimateCanExpand(charCount: number, maxLines: number, containerWidth: number, fontSize: number): boolean {
    if (!containerWidth || !maxLines || !charCount) return true;

    // Average character width is ~0.5-0.6 of font size for most fonts
    const avgCharWidth = fontSize * 0.55;
    const charsPerLine = Math.floor(containerWidth / avgCharWidth);
    const estimatedLines = Math.ceil(charCount / charsPerLine);

    return estimatedLines > maxLines;
}

export function useExpandableText({containerRef, charCount, maxLines, ssrEstimation}: UseExpandableTextOptions): UseExpandableTextReturn {
    const device = useDevice();

    // Get maxLines for current device (tablet falls back to desktop)
    const getMaxLines = (): MaxLinesValue => {
        if (typeof maxLines === "number") return maxLines;
        if (maxLines) {
            return maxLines[device.type] ?? (device.type === "tablet" ? maxLines.desktop : undefined) ?? DEFAULT_MAX_LINES[device.type];
        }
        return DEFAULT_MAX_LINES[device.type];
    };

    const currentMaxLines = getMaxLines();

    // SSR prediction based on device type
    const getSSRCanExpand = (): boolean => {
        if (!charCount) return true; // No char count - show button by default

        // Use provided config or fallback: tablet → desktop → defaults
        const defaultConfig = DEFAULT_SSR_ESTIMATION[device.type];
        const customConfig = ssrEstimation?.[device.type] ?? (device.type === "tablet" ? ssrEstimation?.desktop : undefined);

        const width = customConfig?.width ?? defaultConfig.width;
        const fontSize = customConfig?.fontSize ?? defaultConfig.fontSize ?? 14;

        return estimateCanExpand(charCount, currentMaxLines, width, fontSize);
    };

    const [isExpanded, setIsExpanded] = useState(false);
    const [canExpand, setCanExpand] = useState(getSSRCanExpand);
    const [collapsedHeight, setCollapsedHeight] = useState(0);
    const [expandedHeight, setExpandedHeight] = useState(0);
    const [isReady, setIsReady] = useState(false);
    const [showEllipsis, setShowEllipsis] = useState(true);
    const [isExpandAnimationComplete, setIsExpandAnimationComplete] = useState(false);

    // Listen to transitionend - handle animation completion
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const handleTransitionEnd = (e: TransitionEvent): void => {
            if (e.propertyName === "max-height") {
                if (isExpanded) {
                    // Expand animation complete - remove height constraint for true content height
                    setIsExpandAnimationComplete(true);
                    container.style.maxHeight = "none";
                } else {
                    // Collapse animation complete - show ellipsis
                    setShowEllipsis(true);
                }
            }
        };

        container.addEventListener("transitionend", handleTransitionEnd);
        return () => container.removeEventListener("transitionend", handleTransitionEnd);
    }, [containerRef, isExpanded]);

    // Measure heights on client when element becomes visible
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const measureHeights = (): void => {
            // Measure clamped height on original element
            const clampedRect = container.getBoundingClientRect();
            const clampedHeight = clampedRect.height;

            // Clone element to measure full height without affecting original
            const clone = container.cloneNode(true) as HTMLElement;
            Object.assign(clone.style, {
                position: "absolute",
                visibility: "hidden",
                pointerEvents: "none",
                overflow: "visible",
                height: "auto",
                maxHeight: "none",
                webkitLineClamp: "unset",
                display: "block",
                // IMPORTANT: preserve width so text wraps the same way
                width: `${clampedRect.width}px`,
            });

            // Insert clone after original
            container.after(clone);

            // Measure full height from clone
            requestAnimationFrame(() => {
                const fullHeight = clone.offsetHeight;

                // Remove clone
                clone.remove();

                // Add small buffer to prevent last line from being cut off
                const collapsed = clampedHeight + 4;
                const needsExpand = fullHeight > clampedHeight + 2;

                setCollapsedHeight(collapsed);
                setExpandedHeight(fullHeight);
                setCanExpand(needsExpand);
                setIsReady(true);
            });
        };

        const startMeasurement = (): void => {
            // Wait for fonts to load before measuring
            if (document.fonts) {
                void document.fonts.ready.then(() => {
                    requestAnimationFrame(() => {
                        requestAnimationFrame(measureHeights);
                    });
                });
            } else {
                requestAnimationFrame(() => {
                    requestAnimationFrame(measureHeights);
                });
            }
        };

        // Use IntersectionObserver to measure only when element is visible
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        // Element is visible, start measurement
                        startMeasurement();
                        // Disconnect after first measurement
                        observer.disconnect();
                    }
                });
            },
            {
                rootMargin: "50px", // Start measuring slightly before element enters viewport
            },
        );

        observer.observe(container);

        // Track width - only re-measure if width changed by more than 50px (ignore mobile swipe)
        let lastWidth = window.innerWidth;
        const handleResize = debounce(() => {
            const widthDiff = Math.abs(window.innerWidth - lastWidth);
            if (widthDiff > 50) {
                lastWidth = window.innerWidth;
                setIsReady(false);
                // Wait for React to re-render (remove inline maxHeight) before measuring
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        measureHeights();
                    });
                });
            }
        }, 300);

        window.addEventListener("resize", handleResize);

        return () => {
            observer.disconnect();
            handleResize.clear();
            window.removeEventListener("resize", handleResize);
        };
    }, [containerRef, charCount]);

    const toggle = useCallback((): void => {
        const container = containerRef.current;
        if (!container) return;

        if (isExpanded) {
            // Collapse: set current height, then animate to collapsed
            setIsExpandAnimationComplete(false);
            const currentRect = container.getBoundingClientRect();
            container.style.maxHeight = `${currentRect.height}px`;
            void container.offsetHeight;

            requestAnimationFrame(() => {
                setIsExpanded(false);
            });
        } else {
            // Expand: measure full height using clone for accuracy
            setShowEllipsis(false);

            // Clone element to measure full height
            const containerRect = container.getBoundingClientRect();
            const clone = container.cloneNode(true) as HTMLElement;
            Object.assign(clone.style, {
                position: "absolute",
                visibility: "hidden",
                pointerEvents: "none",
                overflow: "visible",
                height: "auto",
                maxHeight: "none",
                webkitLineClamp: "unset",
                display: "block",
                // IMPORTANT: preserve width so text wraps the same way
                width: `${containerRect.width}px`,
            });

            container.after(clone);

            requestAnimationFrame(() => {
                const freshHeight = clone.offsetHeight;
                clone.remove();

                if (freshHeight !== expandedHeight) {
                    setExpandedHeight(freshHeight);
                }

                // Set starting point for animation
                container.style.maxHeight = `${collapsedHeight}px`;
                void container.offsetHeight;

                // Animate to expanded height
                requestAnimationFrame(() => {
                    setIsExpanded(true);
                });
            });
        }
    }, [isExpanded, containerRef, expandedHeight, collapsedHeight]);

    return {
        isExpanded,
        canExpand,
        collapsedHeight,
        expandedHeight,
        isReady,
        showEllipsis,
        isExpandAnimationComplete,
        toggle,
        maxLines: currentMaxLines,
    };
}
